import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { GroupMemberEntry, TrainingGroupResponse } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useLockBodyScroll } from '../lib/use-lock-body-scroll';
import { Alert, Button, Card, Input } from '../components/ui';
import { OrgTabs } from '../components/OrgTabs';

function MembersDialog({ group, onClose }: { group: TrainingGroupResponse; onClose: () => void }) {
  const { data: members } = useQuery({
    queryKey: ['training', 'groups', group.id, 'members'],
    queryFn: () => api<GroupMemberEntry[]>(`/training/groups/${group.id}/members`, { orgScoped: true }),
  });
  useLockBodyScroll();

  return (
    <div
      className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-sm overflow-y-auto rounded-lg bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Mitglieder — {group.name}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Schließen">
            ✕
          </button>
        </div>
        {members && members.length > 0 ? (
          <ul className="divide-y divide-slate-100 text-sm">
            {members.map((m) => (
              <li key={m.id} className="py-2">
                {m.name}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Keine Mitglieder in dieser Trainingsgruppe.</p>
        )}
      </div>
    </div>
  );
}

export function OrgGroupsPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [newGroupName, setNewGroupName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [showMembersFor, setShowMembersFor] = useState<TrainingGroupResponse | null>(null);

  const { data: groups } = useQuery({
    queryKey: ['training', 'groups'],
    queryFn: () => api<TrainingGroupResponse[]>('/training/groups', { orgScoped: true }),
  });

  const createGroup = useMutation({
    mutationFn: () =>
      api('/training/groups', { method: 'POST', orgScoped: true, body: { name: newGroupName } }),
    onSuccess: () => {
      setNewGroupName('');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['training', 'groups'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Anlegen fehlgeschlagen.'),
  });

  const renameGroup = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api(`/training/groups/${id}`, { method: 'PATCH', orgScoped: true, body: { name } }),
    onSuccess: () => {
      setRenaming(null);
      void qc.invalidateQueries({ queryKey: ['training', 'groups'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Umbenennen fehlgeschlagen.'),
  });

  const deleteGroup = useMutation({
    mutationFn: (id: string) => api(`/training/groups/${id}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['training', 'groups'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Löschen fehlgeschlagen.'),
  });

  const toggleActive = useMutation({
    mutationFn: (group: TrainingGroupResponse) =>
      api(`/training/groups/${group.id}`, {
        method: 'PATCH',
        orgScoped: true,
        body: { isActive: !group.isActive },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['training', 'groups'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Aktualisieren fehlgeschlagen.'),
  });

  return (
    <div className="space-y-4">
      <h1 className="hidden text-xl font-semibold md:block">Verein</h1>
      <OrgTabs />

      <Card title="Trainingsgruppen">
        <p className="mb-3 text-xs text-slate-400">
          Mitglieder werden auf ihrer Detailseite einer oder mehreren Trainingsgruppen zugeordnet.
          Eine Trainingsgruppe wird auf der Trainingsseite genutzt, um Termine anzulegen. Über
          „Bei Registrierung wählbar" steuerst du, ob eine Gruppe im öffentlichen
          Registrierungsformular (Einladungslink) zur Auswahl steht.
        </p>
        {error && <Alert>{error}</Alert>}
        {groups && groups.length > 0 ? (
          <ul className="mb-4 divide-y divide-slate-100">
            {groups.map((g) => (
              <li key={g.id} className="py-2">
                {renaming === g.id ? (
                  <form
                    className="flex items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (renameValue.trim()) renameGroup.mutate({ id: g.id, name: renameValue.trim() });
                    }}
                  >
                    <Input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      className="flex-1"
                    />
                    <Button type="submit" className="px-2 py-1 text-xs" disabled={renameGroup.isPending}>
                      Speichern
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      onClick={() => setRenaming(null)}
                    >
                      Abbrechen
                    </Button>
                  </form>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span>
                      {g.name} — {g.memberCount} Mitglieder
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      {can('training.manage') ? (
                        <label className="flex items-center gap-1.5 text-xs text-slate-500">
                          <input
                            type="checkbox"
                            checked={g.isActive}
                            disabled={toggleActive.isPending}
                            onChange={() => toggleActive.mutate(g)}
                          />
                          Bei Registrierung wählbar
                        </label>
                      ) : (
                        !g.isActive && (
                          <span className="text-xs text-slate-400">Bei Registrierung nicht wählbar</span>
                        )
                      )}
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs"
                        onClick={() => setShowMembersFor(g)}
                      >
                        Mitglieder anzeigen
                      </Button>
                      {can('training.manage') && (
                        <>
                          <Button
                            variant="ghost"
                            className="px-2 py-1 text-xs"
                            onClick={() => {
                              setRenaming(g.id);
                              setRenameValue(g.name);
                            }}
                          >
                            Umbenennen
                          </Button>
                          <Button
                            variant="ghost"
                            className="px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                            disabled={deleteGroup.isPending}
                            onClick={() => {
                              if (confirm(`Trainingsgruppe „${g.name}" wirklich löschen?`)) deleteGroup.mutate(g.id);
                            }}
                          >
                            Löschen
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-4 text-sm text-slate-500">Noch keine Trainingsgruppen angelegt.</p>
        )}

        {can('training.manage') && (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (newGroupName) createGroup.mutate();
            }}
          >
            <Input
              placeholder="Neue Trainingsgruppe (Name)"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
            />
            <Button type="submit" variant="ghost" disabled={!newGroupName || createGroup.isPending}>
              Anlegen
            </Button>
          </form>
        )}
      </Card>

      {showMembersFor && (
        <MembersDialog group={showMembersFor} onClose={() => setShowMembersFor(null)} />
      )}
    </div>
  );
}
